import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Standing guidance, always visible — never a placeholder pretending to be a label. */
  hint?: ReactNode;
  /**
   * A unit shown inside the control, beside the cursor — "sec", "kg", "%".
   *
   * It is drawn `aria-hidden`, because a screen reader that announced both the
   * painted text and the described-by text would say it twice; the same word is
   * carried on `aria-describedby` instead, so it is heard exactly once.
   *
   * A unit, not a hint. Anything longer than two or three characters belongs in
   * `hint`, where it will not crowd what the person is typing.
   */
  suffix?: string;
  error?: string | undefined;
}

export function Field({ label, hint, suffix, error, className, ...rest }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const suffixId = `${id}-suffix`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, suffix ? suffixId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  const input = (
    <input
      id={id}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      className={[
        'bg-surface-raised text-ink min-h-11 rounded-[--radius-control] border px-3 text-base',
        suffix ? 'w-full pr-11' : '',
        error ? 'border-danger' : 'border-border-strong',
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );

  return (
    <div className={['flex flex-col gap-1.5', className ?? ''].join(' ')}>
      <label htmlFor={id} className="text-ink text-sm font-medium">
        {label}
      </label>

      {suffix ? (
        <div className="relative">
          {input}
          {/*
            One element, not two. `aria-hidden` takes it out of the reading
            order, but a node referenced by `aria-describedby` still
            contributes its text to the description — so the unit is painted
            once and announced once.
          */}
          <span
            id={suffixId}
            aria-hidden="true"
            className="text-ink-muted pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm"
          >
            {suffix}
          </span>
        </div>
      ) : (
        input
      )}

      {hint ? (
        <p id={hintId} className="text-ink-muted text-xs">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-danger text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}
